import { formatPrice } from '../utils/currency';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { customerApi } from '../api/apiClient';
import { useLanguage } from '../context/LanguageContext';
import { useCoreT } from '../context/CoreLangContext';

const STATUS_COLOR = {
  Pending:   { bg: 'rgba(234,179,8,.15)',   text: '#fde047' },
  Confirmed: { bg: 'rgba(99,102,241,.15)',  text: '#a5b4fc' },
  Preparing: { bg: 'rgba(249,115,22,.15)',  text: '#fdba74' },
  Ready:     { bg: 'rgba(16,185,129,.15)',  text: '#6ee7b7' },
  Delivered: { bg: 'rgba(100,116,139,.15)', text: '#94a3b8' },
  Cancelled: { bg: 'rgba(239,68,68,.15)',   text: '#fca5a5' },
};

export default function CustomerProfilePage({ restaurant, onClose }) {
  const T = useCoreT();
  const navigate = useNavigate();
  const { customer, logoutCustomer, loginCustomer, saveCustomerProfile } = useAuth();
  const { tu } = useLanguage();
  const [tab,    setTab]    = useState('profile');
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [notif,  setNotif]  = useState(null);

  const [profileForm, setProfileForm] = useState({
    name:        customer?.name    || '',
    phone:       customer?.phone   || '',
    address:     customer?.address || '',
    password:    '',
    newPassword: '',
  });
  const [saving, setSaving] = useState(false);

  const notify = (msg, type = 'success') => {
    setNotif({ msg, type });
    setTimeout(() => setNotif(null), 3000);
  };

  useEffect(() => {
    if (tab === 'orders') {
      setLoadingOrders(true);
      customerApi.getOrders()
        .then(r => setOrders(r.data || []))
        .catch(() => setOrders([]))
        .finally(() => setLoadingOrders(false));
    }
  }, [tab]);

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (profileForm.name.trim()    !== (customer.name    || '')) payload.name    = profileForm.name.trim();
      if (profileForm.phone.trim()   !== (customer.phone   || '')) payload.phone   = profileForm.phone.trim();
      if (profileForm.address.trim() !== (customer.address || '')) payload.address = profileForm.address.trim();
      if (profileForm.newPassword) {
        if (!profileForm.password) { notify('Enter your current password', 'error'); setSaving(false); return; }
        if (profileForm.newPassword.length < 6) { notify('New password must be at least 6 chars', 'error'); setSaving(false); return; }
        payload.password    = profileForm.password;
        payload.newPassword = profileForm.newPassword;
      }
      if (Object.keys(payload).length === 0) { notify('No changes to save'); setSaving(false); return; }
      const res = await customerApi.updateMe(payload);
      // Update the stored customer profile in context/localStorage
      if (res.data) saveCustomerProfile(res.data);
      notify('Profile updated ✓');
      setProfileForm(f => ({ ...f, password: '', newPassword: '' }));
    } catch (e) {
      notify(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logoutCustomer();
    if (onClose) onClose();
    else window.location.href = window.location.origin + '/';
  };

  const pri = restaurant?.primaryColor || '#c8a96e';

  return (
    <div className="cp-backdrop" onClick={onClose}>
      <div className="cp-panel" onClick={e => e.stopPropagation()} style={{ '--pri': pri }}>
        {notif && <div className={`cp-notif ${notif.type}`}>{notif.msg}</div>}

        {/* Header */}
        <div className="cp-header">
          <div className="cp-avatar">{customer?.name?.[0]?.toUpperCase() || '?'}</div>
          <div>
            <div className="cp-name">{customer?.name}</div>
            <div className="cp-email">{customer?.email}</div>
          </div>
          <button className="cp-close" onClick={onClose || (() => navigate('/menu'))}>✕</button>
        </div>

        {/* Tabs */}
        <div className="cp-tabs">
          <button className={`cp-tab ${tab==='profile'?'active':''}`} onClick={() => setTab('profile')}>👤 {tu('profile_tab')}</button>
          <button className={`cp-tab ${tab==='orders' ?'active':''}`} onClick={() => setTab('orders')}>📋 {tu('profile_orders_tab')}</button>
          <button className={`cp-tab ${tab==='favs' ?'active':''}`} onClick={() => setTab('favs')}>♥ {tu('profile_favs_tab')}</button>
        </div>

        {/* Profile Tab */}
        {tab === 'profile' && (
          <div className="cp-body">
            <div className="cp-fields">
              <div className="cp-field">
                <label>Full Name</label>
                <input className="cp-input" value={profileForm.name}
                  onChange={e => setProfileForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="cp-field">
                <label>{tu('profile_phone')}</label>
                <input className="cp-input" value={profileForm.phone} placeholder="e.g. +1 234 567 8900"
                  onChange={e => setProfileForm(f => ({ ...f, phone: e.target.value }))} />
              </div>
              <div className="cp-field">
                <label>{tu('profile_address')}</label>
                <input className="cp-input" value={profileForm.address} placeholder="e.g. 123 Main St, City"
                  onChange={e => setProfileForm(f => ({ ...f, address: e.target.value }))} />
              </div>
              <div className="cp-divider">Change Password <span>(leave blank to keep current)</span></div>
              <div className="cp-field">
                <label>{tu('profile_curr_pass')}</label>
                <input className="cp-input" type="password" value={profileForm.password}
                  onChange={e => setProfileForm(f => ({ ...f, password: e.target.value }))} placeholder="••••••••" />
              </div>
              <div className="cp-field">
                <label>{tu('profile_new_pass')}</label>
                <input className="cp-input" type="password" value={profileForm.newPassword}
                  onChange={e => setProfileForm(f => ({ ...f, newPassword: e.target.value }))} placeholder="••••••••" />
              </div>
            </div>
            <button className="cp-save-btn" onClick={handleSaveProfile} disabled={saving}
              style={{ background: pri }}>
              {saving ? 'Saving…' : tu('profile_save')}
            </button>
            <button className="cp-logout-btn" onClick={handleLogout}>{tu('profile_logout')}</button>
          </div>
        )}

        {/* Orders Tab */}
        {tab === 'favs' && (() => {
          const favIds = customer?.favorites || [];
          const all = [
            ...(restaurant?.products || []),
            ...((restaurant?.branches || []).flatMap(b => b.products || [])),
          ];
          const favProducts = all.filter(p => favIds.includes(String(p._id)));
          return (
            <div className="cp-body">
              {favProducts.length === 0 ? (
                <div className="cp-empty">
                  <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>♡</div>
                  <p>{tu('profile_no_favs')}</p>
                </div>
              ) : (
                <div className="cp-orders">
                  {favProducts.map(p => (
                    <div key={p._id} className="cp-order-card cp-fav-card"
                      onClick={() => { (onClose||(()=>{}))(); navigate(`/item/${p._id}`); }}>
                      {p.image && <img src={p.image} alt={p.name} className="cp-fav-img" />}
                      <div className="cp-fav-info">
                        <div className="cp-fav-name">{p.name}</div>
                        <div className="cp-fav-price">{formatPrice(p.price, restaurant?.currencyCode)}</div>
                      </div>
                      <span className="cp-fav-heart">♥</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {tab === 'orders' && (
          <div className="cp-body">
            {loadingOrders ? (
              <div className="cp-loading">Loading orders…</div>
            ) : orders.length === 0 ? (
              <div className="cp-empty">
                <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>📋</div>
                <p>{tu('profile_no_orders')}</p>
                <button className="cp-browse-btn" onClick={onClose || (() => navigate('/menu'))}
                  style={{ background: pri }}>{tu('profile_browse')}</button>
              </div>
            ) : (
              <div className="cp-orders">
                {orders.map(order => {
                  const sc = STATUS_COLOR[order.status] || STATUS_COLOR.Pending;
                  return (
                    <div key={order._id} className="cp-order-card">
                      <div className="cp-order-top">
                        <span className="cp-order-num">{order.orderNumber}</span>
                        <span className="cp-order-date">{new Date(order.createdAt).toLocaleDateString()}</span>
                        <span className="cp-order-status" style={{ background: sc.bg, color: sc.text }}>
                          {order.status}
                        </span>
                      </div>
                      <div className="cp-order-items">
                        {order.items.map((it, i) => (
                          <div key={i} className="cp-order-item">
                            <span>{it.productName} ×{it.quantity}</span>
                            <span>{formatPrice(it.price * it.quantity, restaurant?.currencyCode)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="cp-order-total">Total: <strong>{formatPrice(order.total, restaurant?.currencyCode)}</strong> <button className="cp-track-link" onClick={() => { (onClose||(()=>{}))(); navigate(`/order/${order._id}`); }}>📡 {tu('track_cta')}</button></div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <style>{`
          .cp-backdrop{position:fixed;inset:0;background:rgba(0,0,0,.65);backdrop-filter:blur(8px);z-index:200;display:flex;align-items:flex-start;justify-content:flex-end}
          .cp-panel{width:380px;height:100vh;background:#1a1a1a;border-left:1px solid rgba(255,255,255,.1);display:flex;flex-direction:column;font-family:'DM Sans',sans-serif;overflow:hidden;position:relative;animation:slideInRight .25s ease}
          @keyframes slideInRight{from{transform:translateX(100%)}to{transform:translateX(0)}}
          @media(max-width:440px){.cp-panel{width:100vw}}
          .cp-notif{position:absolute;top:1rem;left:1rem;right:1rem;z-index:10;padding:.75rem 1rem;border-radius:10px;font-size:.85rem;font-weight:600}
          .cp-notif.success{background:#064e3b;color:#6ee7b7;border:1px solid #10b981}
          .cp-notif.error{background:#7f1d1d;color:#fca5a5;border:1px solid #ef4444}
          .cp-header{display:flex;align-items:center;gap:1rem;padding:1.5rem;border-bottom:1px solid rgba(255,255,255,.07)}
          .cp-avatar{width:48px;height:48px;border-radius:50%;background:var(--pri);display:flex;align-items:center;justify-content:center;font-size:1.25rem;font-weight:700;color:#fff;flex-shrink:0}
          .cp-name{font-weight:700;color:#fff;font-size:1rem}
          .cp-email{font-size:.78rem;color:rgba(255,255,255,.4);margin-top:.1rem;flex:1}
          .cp-close{margin-left:auto;background:none;border:none;color:rgba(255,255,255,.4);cursor:pointer;font-size:1rem;padding:.4rem;border-radius:6px;transition:color .2s;flex-shrink:0}
          .cp-close:hover{color:#fff}
          .cp-tabs{display:flex;gap:.25rem;padding:.75rem 1rem;border-bottom:1px solid rgba(255,255,255,.07)}
          .cp-tab{flex:1;padding:.6rem;background:none;border:none;color:rgba(255,255,255,.45);cursor:pointer;border-radius:8px;font-family:'DM Sans',sans-serif;font-size:.875rem;transition:all .2s}
          .cp-tab.active{background:rgba(255,255,255,.08);color:#fff;font-weight:600}
          .cp-body{flex:1;overflow-y:auto;padding:1.5rem;display:flex;flex-direction:column;gap:1rem}
          .cp-fields{display:flex;flex-direction:column;gap:.875rem}
          .cp-field{display:flex;flex-direction:column;gap:.35rem}
          .cp-field label{font-size:.7rem;color:rgba(255,255,255,.4);text-transform:uppercase;letter-spacing:.07em}
          .cp-input{background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:10px;padding:.75rem 1rem;color:#fff;font-family:'DM Sans',sans-serif;font-size:.9rem;outline:none;transition:border-color .2s}
          .cp-input:focus{border-color:var(--pri)}
          .cp-divider{font-size:.78rem;color:rgba(255,255,255,.35);padding:.75rem 0 .25rem;border-top:1px solid rgba(255,255,255,.07);margin-top:.5rem}
          .cp-divider span{color:rgba(255,255,255,.2);font-size:.72rem}
          .cp-save-btn{padding:.9rem;border:none;border-radius:12px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.95rem;font-weight:700;cursor:pointer;transition:opacity .2s}
          .cp-save-btn:hover:not(:disabled){opacity:.85}
          .cp-save-btn:disabled{opacity:.5;cursor:not-allowed}
          .cp-logout-btn{padding:.8rem;background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.2);border-radius:12px;color:#f87171;font-family:'DM Sans',sans-serif;font-size:.9rem;cursor:pointer;transition:all .2s}
          .cp-logout-btn:hover{background:rgba(239,68,68,.18);border-color:rgba(239,68,68,.4)}
          .cp-loading{text-align:center;padding:3rem;color:rgba(255,255,255,.4)}
          .cp-empty{text-align:center;padding:3rem 1.5rem;color:rgba(255,255,255,.4)}
          .cp-empty p{margin-bottom:1.5rem}
          .cp-browse-btn{padding:.8rem 2rem;border:none;border-radius:12px;color:#fff;font-family:'DM Sans',sans-serif;font-size:.9rem;font-weight:700;cursor:pointer;transition:opacity .2s}
          .cp-orders{display:flex;flex-direction:column;gap:.875rem}
          .cp-order-card{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:14px;padding:1rem;display:flex;flex-direction:column;gap:.75rem}
          .cp-order-top{display:flex;align-items:center;gap:.75rem}
          .cp-order-num{font-weight:700;color:var(--pri);font-size:.85rem}
          .cp-order-date{font-size:.75rem;color:rgba(255,255,255,.35);flex:1}
          .cp-order-status{padding:.2rem .65rem;border-radius:10px;font-size:.7rem;font-weight:700}
          .cp-order-items{display:flex;flex-direction:column;gap:.35rem}
          .cp-order-item{display:flex;justify-content:space-between;font-size:.82rem;color:rgba(255,255,255,.65)}
          .cp-order-total{font-size:.82rem;color:rgba(255,255,255,.5);text-align:right;padding-top:.5rem;border-top:1px solid rgba(255,255,255,.06)}
          .cp-order-total strong{color:#fff}
          .cp-track-link{margin-inline-start:.6rem;background:none;border:1px solid rgba(255,255,255,.15);border-radius:8px;color:rgba(255,255,255,.7);font-size:.72rem;padding:.25rem .6rem;cursor:pointer}
          .cp-fav-card{display:flex;align-items:center;gap:.85rem;cursor:pointer}
          .cp-fav-img{width:54px;height:54px;border-radius:10px;object-fit:cover;flex-shrink:0}
          .cp-fav-info{flex:1;min-width:0}
          .cp-fav-name{font-weight:600;color:#fff}
          .cp-fav-price{font-size:.8rem;color:rgba(255,255,255,.5)}
          .cp-fav-heart{color:#f87171;font-size:1.1rem}
        `}</style>
      </div>
    </div>
  );
}
